import { Ref, ref } from 'vue';
import { useAuth } from './useAuth';
import { useNotifications } from './useNotifications';

const isDevelopment = import.meta.env.DEV;
const API_URL = isDevelopment ? 'http://localhost:3001/api/' : '/api/';

interface RequestSettings {
  baseUrl?: string
  headers?: Record<string, string>
}

interface ApiHook {
  data: Ref<any>
  loading: Ref<boolean>
  get: (url: string, settings?: RequestSettings) => Promise<any>
  post: (url: string, body: any, settings?: RequestSettings) => Promise<any>
  put: (url: string, body: any, settings?: RequestSettings) => Promise<any>
  patch: (url: string, body: any, settings?: RequestSettings) => Promise<any>
  remove: (url: string, body?: any, settings?: RequestSettings) => Promise<any>
}

export function useApi(): ApiHook {
  const data: Ref<any> = ref(null);
  const loading: Ref<boolean> = ref(false);
  const { getToken } = useAuth();
  const { notify } = useNotifications();

  async function request(
    method: string,
    url: string,
    body: any = null,
    settings: RequestSettings = {}
  ): Promise<any> {
    loading.value = true;

    const auth0Token = await getToken();
    try {
      const baseUrl = settings.baseUrl || API_URL;
      url = removeStartingSlash(url);

      const headers: Record<string, string> = {
        ...settings.headers
      };

      // Only set Content-Type to application/json if body is not FormData
      if (!(body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      if (auth0Token) {
        headers.Authorization = `Bearer ${auth0Token}`;
      }

      const response = await fetch(baseUrl + url, {
        method,
        body: body instanceof FormData ? body : (body ? JSON.stringify(body) : null),
        headers
      });

      let responseData;
      const contentType = response.headers.get('content-type');
      
      if (contentType?.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      if (!response.ok) {
        const errorMessage = responseData?.message || responseData?.error || responseData || 'API Request Failed';
        throw new Error(errorMessage);
      }

      data.value = responseData;
      return responseData;
    } catch (err) {
      console.error('Request error:', err);
      const error = err as Error;
      notify({ 
        message: error.message,
        type: 'ERROR'
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  function removeStartingSlash(url: string): string {
    return url.startsWith('/') ? url.slice(1) : url;
  }

  return {
    data,
    loading,
    get: (url: string, settings?: RequestSettings) => request('GET', url, null, settings),
    post: (url: string, body: any, settings?: RequestSettings) => request('POST', url, body, settings),
    put: (url: string, body: any, settings?: RequestSettings) => request('PUT', url, body, settings),
    patch: (url: string, body: any, settings?: RequestSettings) => request('PATCH', url, body, settings),
    remove: (url: string, body?: any, settings?: RequestSettings) => request('DELETE', url, body, settings)
  };
}